// import state variables from asset-manager-state.js
import {
  sketchableMapLayersToAdd,
  allSketchableLayerIds,
  sketchableLayersWithNoAdditionRequired,
  graphicLayers,
  createdAssets,
  validSketchableLayers,
  setCreatedAssetsAreValid,
  getCreatedAssetsAreValid,
} from "../asset-manager-state.js";
// import from asset-manager-functions.js
import {
  monitorLayerVisibility,
  renderValidityMessage,
} from "./asset-manager-functions.js";
// import asset-manager-map-layer-data-display component
import "../custom-elements/asset-manager-map-layer-data-display.js";

export const dispatchCreatedAssets = (createdAssets) => {
  const event = new CustomEvent("createdAssetsAreValidIsTrue", {
    detail: { createdAssets },
  });
  document.dispatchEvent(event);
};

// custom event listener to signal when createdAssets are not valid
export const secureCreatedAssets = () => {
  const createdAssetsAreValid = getCreatedAssetsAreValid();
  const event = new CustomEvent("createdAssetsAreValidIsFalse", {
    detail: { createdAssetsAreValid },
  });
  document.dispatchEvent(event);
};

// event listener to capture sketchable layer data from sketchable-map-layer.js
export const captureSketachableMapLayers = () => {
  document.addEventListener("sketchableLayerDetailsProvided", (event) => {
    const sketchableLayer = event.detail;
    sketchableMapLayersToAdd.push(sketchableLayer);
  });
};

const enableSketchForLayer = (layer) => {
  const layerGeometryType = layer.sketchType[0];
  const modeStatusBanner = document.getElementById("mode-status-banner");
  // Clear existing geometry icon if present
  const existingGeometryIcon = modeStatusBanner.querySelector(
    ".current-sketch-geometry-icon",
  );
  if (existingGeometryIcon) {
    existingGeometryIcon.remove();
  }

  const geometryIcon = document.createElement("calcite-icon");
  geometryIcon.setAttribute("scale", "s");
  geometryIcon.classList.add("current-sketch-geometry-icon");
  geometryIcon.setAttribute(
    "icon",
    layerGeometryType === "polygon"
      ? "polygon"
      : layerGeometryType === "polyline"
        ? "line"
        : "pin",
  );

  const modeStatusTextSpan = document.getElementById("mode-status-text-span");
  modeStatusTextSpan.innerText = `Sketch Mode Enabled for ${layer.graphicsLayer.formattedLayerName}.`;
  const modeStatusIconSpan = document.getElementById("mode-status-icon-span");
  modeStatusIconSpan.innerHTML = ""; // Clear existing icons
  modeStatusIconSpan.appendChild(geometryIcon);
  const enableSketchButtons = document.querySelectorAll(
    ".enable-sketch-button",
  );
  enableSketchButtons.forEach((element) => {
    element.classList.remove("sketch-button-shadow", "pointer-events-none");
    element.disabled = false;
  });
  const otherEnableSketchButtons = document.querySelectorAll(
    `.enable-sketch-button:not(#enable-sketch-btn-${layer.graphicsLayer.formattedLayerName.replace(/[-, _]/g, "-")})`,
  );
  otherEnableSketchButtons.forEach((element) => {
    const buttonIcon = element.querySelector("calcite-icon");
    buttonIcon.setAttribute("icon", "pencil");
  });
  // Sanitize the layer title the same way as in asset-manager-map-layer-data-display.js
  const sanitizedLayerName = layer.name
    .replace(/[-, _]/g, " ")
    .replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
    )
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9-_]/g, "");
  const layerEnableSketchButton = document.getElementById(
    `enable-sketch-btn-${sanitizedLayerName}`,
  );
  layerEnableSketchButton.classList.add(
    "sketch-button-shadow",
    "pointer-events-none",
  );
  layerEnableSketchButton.disabled = true;
  // Find the <calcite-icon> inside the button
  const buttonIcon = layerEnableSketchButton.querySelector(
    `#enable-sketch-icon-${sanitizedLayerName} calcite-icon`,
  );
  if (buttonIcon) {
    buttonIcon.setAttribute(
      "icon",
      layerGeometryType === "polygon"
        ? "polygon"
        : layerGeometryType === "polyline"
          ? "line"
          : "pin",
    );
  }

  const layerAssetTypeIcon = document.getElementById(
    `${sanitizedLayerName}-layer-asset-type-icon`,
  );
  if (layerAssetTypeIcon) {
    layerAssetTypeIcon.innerHTML = `<calcite-icon icon="${
      layerGeometryType === "polygon"
        ? "polygon"
        : layerGeometryType === "polyline"
          ? "line"
          : "pin"
    }" />`;
  }

  const otherLayerAssetTypeIcons = document.querySelectorAll(
    ".layer-data-display-asset-type-icon-span-sketch",
  );
  otherLayerAssetTypeIcons.forEach((element) => {
    if (element.id !== `${sanitizedLayerName}-layer-asset-type-icon`) {
      element.innerHTML = `<calcite-icon icon="pencil" />`;
    }
  });

  const sketch = document.getElementById("asset-manager-sketch");
  if (sketch && sketch.shadowRoot) {
    const style = document.createElement("style");
    style.textContent = `
    .esri-sketch > div:first-of-type {
      border: 2px solid #174054 !important;
      box-sizing: border-box;
    }
    calcite-action-group:has(calcite-action[data-action-key="pointer-select"]) {
      border-style: none !important;
    }  
  `;
    sketch.shadowRoot.appendChild(style);
  }

  if (sketch) {
    sketch.cancel();
  }

  const sketchType = layer.sketchType;
  sketch.availableCreateTools = sketchType;
  console.log("Setting sketch availableCreateTools to:", sketchType);
  sketch.removeAttribute("hidden");
  const mapContainer = document.getElementById("viewDiv");
  mapContainer.style.pointerEvents = "auto";
  // Set the sketch widget's layer to the correct GraphicsLayer
  if (layer.graphicsLayer) {
    sketch.layer = layer.graphicsLayer;
  } else {
    console.warn("No graphicsLayer found on layer object!");
  }
  setTimeout(() => {
    // Collect all shadow DOM buttons into an array
    const shadowButtons = [];
    const collectShadowButtons = (node) => {
      if (!node) return;
      if (node.querySelectorAll) {
        const btns = node.querySelectorAll("button");
        if (btns.length) {
          shadowButtons.push(...btns);
        }
      }
      if (node.shadowRoot) {
        collectShadowButtons(node.shadowRoot);
      }
      if (node.children) {
        Array.from(node.children).forEach((child) =>
          collectShadowButtons(child),
        );
      }
    };

    collectShadowButtons(sketch);
    const targetButton = shadowButtons.find(
      (b) =>
        b.getAttribute("aria-label") &&
        b.getAttribute("aria-label").toLowerCase().includes(sketchType),
    );
    if (targetButton) {
      targetButton.click();
    } else {
      console.warn(
        `Could not find the sketch tool button for type: ${sketchType}`,
      );
    }
  }, 40);
};

const hideOrShowSketchableLayer = (layerName) => {
  const layer = graphicLayers.find(
    (lyr) => lyr.layerProperties.layerName === layerName,
  );
  if (layer) {
    layer.visible = !layer.visible;
  } else {
    console.warn(`Layer ${layerName} not found in graphicLayers array!`);
  }
};

export const addSketchableMapLayer = async ({
  sketchableMapLayer,
  map,
  view,
  reactiveUtils,
}) => {
  const GraphicsLayer = await $arcgis.import(
    "@arcgis/core/layers/GraphicsLayer.js",
  );

  const layerMinScale = parseInt(sketchableMapLayer.minScale, 10) || 0;
  const layerMaxScale = parseInt(sketchableMapLayer.maxScale, 10) || 0;

  const sketchableGraphicLayer = new GraphicsLayer({
    title: sketchableMapLayer.name,
    maxAssetsAllowed: parseInt(sketchableMapLayer.maximum),
    minAssetsRequired: parseInt(sketchableMapLayer.minimum),
    visible: true,
    layerName: sketchableMapLayer.name,
    formattedLayerName: sketchableMapLayer.name.replace(/[-]/g, " "),
    minScale: sketchableMapLayer.minScale,
    maxScale: sketchableMapLayer.maxScale,
    sketchType: sketchableMapLayer.sketchType,
    layerProperties: {
      layerId: sketchableMapLayer.id,
      layerName: sketchableMapLayer.name,
      formattedLayerName: sketchableMapLayer.name.replace(/[-]/g, " "),
      minAssetsRequired: parseInt(sketchableMapLayer.minimum),
      maxAssetsAllowed: parseInt(sketchableMapLayer.maximum),
      isSketchable: true,
      minScale: layerMinScale,
      maxScale: layerMaxScale,
      sketchType: sketchableMapLayer.sketchType,
    },
  });
  // After creating the GraphicsLayer:
  sketchableGraphicLayer.config = sketchableMapLayer;
  const sketchableGraphicLayerId = `${sketchableGraphicLayer.layerProperties.layerName}-${sketchableGraphicLayer.id}`;
  allSketchableLayerIds.push(sketchableGraphicLayerId);
  graphicLayers.push(sketchableGraphicLayer);
  // Attach the GraphicsLayer instance to the layer object
  sketchableMapLayer.graphicsLayer = sketchableGraphicLayer;
  sketchableGraphicLayer.id = sketchableGraphicLayerId;

  map.add(sketchableGraphicLayer);
  const layerName = sketchableGraphicLayer.layerProperties.layerName;
  const formattedLayerName =
    sketchableGraphicLayer.layerProperties.formattedLayerName;
  const minAssetsRequired = parseInt(
    sketchableGraphicLayer.layerProperties.minAssetsRequired,
  );
  if (minAssetsRequired === 0) {
    sketchableLayersWithNoAdditionRequired.push(sketchableGraphicLayerId);
    validSketchableLayers.push(sketchableGraphicLayerId);
  }
  if (validSketchableLayers.length === allSketchableLayerIds.length) {
    setCreatedAssetsAreValid(true);
  }
  const maxAssetsAllowed = parseInt(
    sketchableGraphicLayer.layerProperties.maxAssetsAllowed,
  );

  const layerDataContainer = document.getElementById("layer-data-container");
  layerDataContainer.classList.add("stat-group");

  view.on("layerview-create", function (event) {
    if (event.layer === sketchableGraphicLayer) {
      monitorLayerVisibility(
        reactiveUtils,
        event.layerView,
        sketchableGraphicLayer,
        layerName,
        formattedLayerName,
        layerMinScale,
        layerMaxScale,
      );
    }
  });

  const mapLayerDataDisplay = document.createElement(
    "asset-manager-map-layer-data-display",
  );
  mapLayerDataDisplay.setAttribute("data-layer-id", sketchableGraphicLayerId);
  mapLayerDataDisplay.data = {
    layerName: layerName,
    formattedLayerName: formattedLayerName,
    mapDataLayerId: sketchableGraphicLayerId,
    minAssetsRequired: minAssetsRequired,
    maxAssetsAllowed: maxAssetsAllowed,
    enableSketchHandler: enableSketchForLayer,
    showHideHandler: hideOrShowSketchableLayer,
    isSketchable: true,
    layerMinScale: layerMinScale,
    layerMaxScale: layerMaxScale,
    availableCreateTools: sketchableMapLayer.sketchType,
    layer: sketchableMapLayer, // <-- config object (for sketching logic)
    arcgisLayer: sketchableGraphicLayer, // <-- the ArcGIS layer (for zoom alert)
    view: view,
  };
  layerDataContainer.appendChild(mapLayerDataDisplay);
};

export const validateCreatedAssets = () => {
  if (validSketchableLayers.length === allSketchableLayerIds.length) {
    setCreatedAssetsAreValid(true);
    dispatchCreatedAssets(createdAssets);
  } else {
    setCreatedAssetsAreValid(false);
    secureCreatedAssets();
  }
  renderValidityMessage();
};

export const updateLayerRequirementDisplay = (asset) => {
  const layerId = asset.attributes.layerId;
  const layer = graphicLayers.find(
    (graphicLayer) => graphicLayer.id === layerId,
  );
  if (!layer) {
    console.warn(`Layer with ID ${layerId} not found in graphicLayers array!`);
    return;
  }
  const layerAssetMin = parseInt(layer.layerProperties.minAssetsRequired);
  const totalLayerAssetsCreated = createdAssets.filter(
    (createdAsset) => createdAsset.attributes.layerId === `${layerId}`,
  ).length;

  const minAssetMessageElement = document.getElementById(
    `${layerId}-min-asset-required-message`,
  );

  if (totalLayerAssetsCreated >= layerAssetMin) {
    minAssetMessageElement.classList.remove("label-error");
    minAssetMessageElement.classList.add("label-success");
    minAssetMessageElement.title = `Minimum requirements met for ${asset.attributes.formattedLayerName} layer`;
    if (!validSketchableLayers.includes(layerId)) {
      validSketchableLayers.push(layerId);
    }
    validateCreatedAssets();
  } else {
    minAssetMessageElement.classList.remove("label-success");
    minAssetMessageElement.classList.add("label-error");
    minAssetMessageElement.title = `Minimum requirements not met for ${asset.attributes.formattedLayerName} layer`;
    const layerIndex = validSketchableLayers.indexOf(layerId);
    if (layerIndex !== -1) {
      validSketchableLayers.splice(layerIndex, 1);
    }
    validateCreatedAssets();
  }
  const assetCountDisplay = document.querySelector(
    `asset-manager-map-layer-data-display[data-layer-id="${layerId}"]`,
  );
  if (assetCountDisplay) {
    assetCountDisplay.assetCount = totalLayerAssetsCreated;
  }
};

function removeLabelForAsset(assetId) {
  // Find the asset in createdAssets to get its layer
  const asset = createdAssets.find((a) => a.attributes.id === assetId);
  if (!asset || !asset.layer || !asset.layer.graphics) return;
  // Find the label graphic by relatedAssetId
  const label = asset.layer.graphics.find(
    (g) => g.attributes && g.attributes.relatedAssetId === assetId,
  );
  if (label) {
    asset.layer.graphics.remove(label);
  }
}

const handleRemoveSketchedAsset = (assetId) => {
  removeLabelForAsset(assetId);
  const assetIndex = createdAssets.findIndex(
    (asset) => asset.attributes.id === assetId,
  );
  if (assetIndex !== -1) {
    const asset = createdAssets[assetIndex];
    // Remove from the graphics layer
    const layer = asset.layer;
    if (layer && layer.graphics) {
      layer.graphics.remove(asset);
    }
    // Remove from createdAssets array
    createdAssets.splice(assetIndex, 1);
    // Remove the list item from the DOM
    const listItem = document.getElementById(assetId);
    if (listItem) listItem.remove();
    // If the list is now empty, add back the "None added" item
    const layerAssetList = document.getElementById(asset.attributes.layerId);
    if (layerAssetList && layerAssetList.children.length === 0) {
      const noneAddedItem = document.createElement("li");
      noneAddedItem.title = `No assets added for ${asset.attributes.formattedLayerName} layer`;
      noneAddedItem.textContent = "None added";
      layerAssetList.appendChild(noneAddedItem);
    }
    updateLayerRequirementDisplay(asset);
  }
  validateCreatedAssets();
};

const renderCreatedAssetLabel = (graphic) => {
  const layerAssetList = document.getElementById(graphic.attributes.layerId);
  if (layerAssetList) {
    const formattedLayerName = graphic.layer.formattedLayerName || "Unknown";
    const firstLi = layerAssetList.querySelector("li");
    if (
      firstLi &&
      firstLi.textContent.trim().toLowerCase().startsWith("none")
    ) {
      firstLi.remove();
    }
    const listItem = document.createElement("li");
    listItem.id = graphic.attributes.id;
    listItem.title = `Proposed ${formattedLayerName} added with ID: ${graphic.attributes.id}`;
    const listItemIndex = Array.from(layerAssetList.children).length;
    const listItemPosition = listItemIndex + 1;
    const listItemContentSpan = document.createElement("span");
    listItemContentSpan.classList.add("asset-list-item-content-span");
    listItemContentSpan.textContent = `${formattedLayerName} ${listItemPosition}`;
    const listItemRemoveButton = document.createElement("button");
    listItemRemoveButton.setAttribute("type", "button");
    listItemRemoveButton.setAttribute(
      "aria-label",
      `Remove asset ${graphic.attributes.id}`,
    );
    listItemRemoveButton.setAttribute(
      "title",
      `Remove asset ${graphic.attributes.id}`,
    );
    listItemRemoveButton.setAttribute(
      "id",
      `remove-${graphic.attributes.id}-btn`,
    );
    listItemRemoveButton.classList.add(
      "pull-right",
      "link-button",
      "small-button",
      "red-button",
      "transparent-button",
      "remove-asset-btn",
    );
    const removeIcon = document.createElement("calcite-icon");
    removeIcon.setAttribute("icon", "x-circle");
    removeIcon.setAttribute("scale", "s");
    listItemRemoveButton.appendChild(removeIcon);

    const removeButtonTextSpan = document.createElement("span");
    removeButtonTextSpan.className = "remove-button-text";
    removeButtonTextSpan.textContent = "Remove";
    listItemRemoveButton.appendChild(removeButtonTextSpan);

    // --- Remove asset event listener ---
    listItemRemoveButton.addEventListener("click", () => {
      handleRemoveSketchedAsset(graphic.attributes.id);
      renderValidityMessage();
    });
    // --- Make the list item clickable to zoom to asset ---
    listItemContentSpan.style.cursor = "pointer";
    listItemContentSpan.addEventListener("click", () => {
      // Try to get the view from the graphicsLayer or pass it in as needed
      const arcGisMap = document.querySelector("arcgis-map");
      const view = arcGisMap.view;
      if (view && graphic.geometry) {
        if (graphic.geometry.type === "point") {
          view.goTo(
            {
              center: [graphic.geometry.longitude, graphic.geometry.latitude],
              zoom: 17,
            },
            { duration: 800 },
          );
        } else if (graphic.geometry.extent) {
          // For polygons/lines, zoom to centroid
          view.goTo(
            {
              target: graphic.geometry.extent.center,
              zoom: 17,
            },
            { duration: 800 },
          );
        } else {
          view.goTo(graphic.geometry, { duration: 800 });
        }
      }
      if (!view) {
        console.warn("Map view not available for zooming to asset.");
      }
    });
    listItem.appendChild(listItemContentSpan);
    listItem.appendChild(listItemRemoveButton);
    layerAssetList.appendChild(listItem);
  }
};

export const sketchAsset = (sketchComponent) => {
  sketchComponent.addEventListener("arcgisCreate", async (event) => {
    if (event.detail.state !== "complete") return; // Only handle completed graphics
    const graphic = event.detail.graphic;
    const config = sketchComponent.layer.config;
    const layerAssetMax =
      sketchComponent.layer.maxAssetsAllowed ??
      (sketchComponent.layer.layerProperties &&
        sketchComponent.layer.layerProperties.maxAssetsAllowed);
    const totalLayerAssetsCreated = createdAssets.filter(
      (createdAsset) =>
        createdAsset.attributes.layerId === `${sketchComponent.layer.id}`,
    ).length;
    if (totalLayerAssetsCreated >= layerAssetMax) {
      alert(
        `Maximum assets allowed for ${sketchComponent.layer.layerProperties.formattedLayerName} layer reached!`,
      );
      console.warn(
        `Cannot add more assets to ${sketchComponent.layer.layerProperties.formattedLayerName} layer. Maximum of ${layerAssetMax} reached.`,
      );
      if (sketchComponent.layer && sketchComponent.layer.graphics) {
        sketchComponent.layer.graphics.remove(graphic);
      }
      return;
    }
    graphic.attributes = {
      assetLabel: config.labelPrefix
        ? `${config.labelPrefix} ${totalLayerAssetsCreated + 1}`
        : `${config.graphicsLayer.formattedLayerName} ${
            totalLayerAssetsCreated + 1
          }`,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      status: "Proposed",
      layerName: config.name,
      formattedLayerName: config.graphicsLayer.formattedLayerName,
      layerId: sketchComponent.layer.id,
      geometryType: graphic.geometry.type,
      geometryString: JSON.stringify(graphic.geometry),
    };

    const Graphic = await $arcgis.import("@arcgis/core/Graphic.js");

    // Create the label text
    const labelText = config.labelPrefix
      ? `${config.labelPrefix} ${totalLayerAssetsCreated + 1}`
      : `${graphic.attributes.formattedLayerName} ${
          totalLayerAssetsCreated + 1
        }`;

    const labelSymbol = {
      type: "text",
      color: "black",
      haloColor: "white",
      haloSize: "1px",
      text: labelText,
      xoffset: 0,
      yoffset: 12,
      font: {
        size: 12,
        family: "sans-serif",
        weight: "bold",
      },
    };

    // For points, use the geometry directly; for lines/polygons, use centroid
    let labelGeometry = graphic.geometry;
    if (graphic.geometry.type !== "point" && graphic.geometry.extent) {
      labelGeometry = graphic.geometry.extent.center;
    }

    // store the asset's ID in the label's attributes
    const labelGraphic = new Graphic({
      geometry: labelGeometry,
      symbol: labelSymbol,
      attributes: {
        relatedAssetId: graphic.attributes.id,
      },
    });
    sketchComponent.layer.graphics.add(labelGraphic);
    graphic.attributes.labelGraphicId = labelGraphic.uid;
    if (!sketchComponent.availableCreateTools.includes(graphic.geometry.type)) {
      console.warn(
        `Created graphic type ${graphic.geometry.type} is not allowed in this layer!`,
      );
      if (sketchComponent.layer && sketchComponent.layer.graphics) {
        sketchComponent.layer.graphics.remove(graphic);
      }
      return;
    }
    createdAssets.push(graphic);
    renderCreatedAssetLabel(graphic);
    updateLayerRequirementDisplay(graphic);
  });
};
